import React, { FC, Fragment, useState, ChangeEvent, useEffect } from 'react';
import { Box, FormControlLabel, Tab, Typography, Switch, Alert, Link, Button } from '@mui/material';
import { LoadingButton, TabContext, TabList } from '@mui/lab';
import { ApolloCache } from '@apollo/client';
import { LabClient } from 'models/client-directory';
import { useMutation } from '@apollo/client';
import { useNavigate, useLocation, useMatch } from 'react-router-dom';
import { modifyInvoiceSettingsTemplate, modifyInvoiceEnabledTemplate } from 'apollo/templates/client-directory';
import { useTranslation } from 'react-i18next';
import InvoiceContent from './invoice-content/invoice-content.component';
import InvoiceSettingsForm from './invoice-settings-form/invoice-settings-form.component';
import BackdropLoader from 'components/shared/backdrop-loader/backdrop-loader.component';
import { Link as RouterLink } from 'react-router-dom';
import InvoiceContactInformation from './invoice-contact-information/invoice-contact-information.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import ClinicInvoiceContent from './invoice-content/clinic-invoice-content/clinic-invoice-content.component';
import { Organization } from 'models/organization';
import { emptyInvoiceSettingsData } from './invoice-settings-form/form-helper';

type InvoicesProps = {
    labClient: LabClient;
    onInvoiceSettingsSaved?: () => void;
    currentLab: Organization;
};
const Invoices: FC<InvoicesProps> = ({ labClient, onInvoiceSettingsSaved, currentLab }: InvoicesProps) => {
    const { t: translate } = useTranslation(['common', 'client-directory, organization']);
    const [invoiceEnabled, setInvoiceEnabled] = useState<boolean>(false);
    const [allInvoiceSettings, setAllInvoiceSettings] = useState<Record<string, any>>({});
    const [isOverriding, setIsOverriding] = useState<boolean>(false);
    const [tabValidity, setTabValidity] = useState({
        content: true,
        settings: true,
        contactInformation: true,
    });

    const handleInvoiceSettingsChange = (settings: Record<string, any>) => {
        setAllInvoiceSettings((prev) => ({ ...prev, ...settings }));
    };

    const handleContentValidityChange = (isValid: boolean) => {
        setTabValidity((prev) => ({ ...prev, content: isValid }));
    };

    const handleSettingsValidityChange = (isValid: boolean) => {
        setTabValidity((prev) => ({ ...prev, settings: isValid }));
    };

    const handleContactInformationValidityChange = (isValid: boolean) => {
        setTabValidity((prev) => ({ ...prev, contactInformation: isValid }));
    };

    const isInvoiceSettingsValid = tabValidity.content && tabValidity.settings && tabValidity.contactInformation;
    const navigate = useNavigate();
    const location = useLocation();
    const [tabState, setTabState] = useState('#content');
    const match = useMatch('/client-directory/clients/:id/:tab');
    const { showBoundary } = useDenthubErrorBoundary();

    useEffect(() => {
        setTabState(location.hash);
    }, [location]);

    useEffect(() => {
        setInvoiceEnabled(labClient.invoiceEnabled || false);
        setIsOverriding(labClient.useOwnInvoiceSettings || false);
        setAllInvoiceSettings((prev) => ({
            ...prev,
            settings: emptyInvoiceSettingsData(currentLab, labClient),
            contactInformation: {
                email: labClient?.invoiceSettings?.contactInformation?.email || '',
                name: labClient?.invoiceSettings?.contactInformation?.name || '',
                phone: labClient?.invoiceSettings?.contactInformation?.phone || '',
            },
        }));
    }, [labClient]);

    const [updateSettings, { loading }] = useMutation(modifyInvoiceEnabledTemplate, {
        onCompleted: () => {
            if (onInvoiceSettingsSaved) {
                onInvoiceSettingsSaved();
            }
        },
        onError: (error) => {
            showBoundary(error);
        },
        update(cache: ApolloCache<LabClient>) {
            // INFO: bust, order-review, invoices, and clinic list cache
            // https://github.com/apollographql/apollo-client/issues/6795
            // READ: https://www.apollographql.com/docs/react/caching/garbage-collection/
            // https://danreynolds.ca/tech/2020/05/04/Apollo-3-Client-Cache/
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'clinicsForLab', args: { filters: { invoiceEnabled: true } } });
            // TODO: Ask if correct way to update cache for labClients
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'labClients', args: { filters: { invoiceEnabled: true } } });
            cache.evict({
                id: 'ROOT_QUERY',
                fieldName: 'clinicsForLab',
                args: { filters: { invoiceEnabled: true, isCollective: true } },
            });
            // TODO: Ask if correct way to update cache for labClients
            cache.evict({
                id: 'ROOT_QUERY',
                fieldName: 'labClients',
                args: { filters: { invoiceEnabled: true, isCollective: true } },
            });
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'invoices' });
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'reviewOrders' });
            cache.gc(); // To remove unreferenced Objects from cache after evict
            localStorage.setItem('reviewOrderListFilter', '{}');
            localStorage.setItem('invoiceListFilter', '{}');
        },
    });

    const [updateInvoiceSettings, { loading: invoiceSettingsLoading }] = useMutation(modifyInvoiceSettingsTemplate, {
        onCompleted: () => {
            if (onInvoiceSettingsSaved) {
                onInvoiceSettingsSaved();
            }
        },
        onError: (error) => {
            showBoundary(error);
        },
    });

    const handleTabChange = (_: unknown, newTabState: string) => {
        if (tabState !== newTabState) {
            navigate(`${match?.pathnameBase}${newTabState}`);
            setTabState(newTabState);
        }
    };
    const invoiceEnabledToggle = () => {
        updateSettings({ variables: { clientId: labClient.customerId, invoiceEnabled } });
    };

    const handleOverrideToggle = (event: ChangeEvent<HTMLInputElement>) => {
        const newOverrideValue = event.target.checked;
        setIsOverriding(newOverrideValue);
        updateInvoiceSettings({
            variables: {
                clientId: labClient.customerId,
                settings: {
                    invoiceEnabled,
                    useOwnInvoiceSettings: newOverrideValue,
                },
            },
        });
    };

    const handleSaveInvoiceSettings = () => {
        updateInvoiceSettings({
            variables: {
                clientId: labClient.customerId,
                settings: {
                    invoiceEnabled,
                    ...allInvoiceSettings,
                },
            },
        });
    };

    return (
        <Fragment>
            <Typography mb={1} variant="h2">
                {translate('client-directory:INVOICING')}
            </Typography>
            {loading ? <BackdropLoader isOpen={loading} /> : undefined}

            {labClient.parentHasInvoiceConfig && (
                <Box mb={2}>
                    <Alert
                        severity="info"
                        action={<Switch color="secondary" checked={isOverriding} onChange={handleOverrideToggle} />}
                    >
                        {translate('client-directory:PARENT_HAS_INVOICE_CONFIG')}
                    </Alert>
                </Box>
            )}

            {labClient.isWriteAllowed || isOverriding ? (
                <Fragment>
                    {invoiceEnabled ? (
                        <Fragment>
                            <Box mb={2}>
                                <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {translate('client-directory:SAVE_INVOICE_SETTING_INFO')}
                                </Alert>
                            </Box>
                            <TabContext value={tabState}>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <TabList onChange={handleTabChange} indicatorColor="primary" textColor="primary">
                                        <Tab label={translate('client-directory:INVOICE_CONTENT')} value="#content" />
                                        <Tab label={translate('client-directory:INVOICE_SETTINGS')} value="#settings" />
                                        <Tab
                                            label={translate('client-directory:CONTACT_INFORMATION')}
                                            value="#contactInformation"
                                        />
                                    </TabList>
                                    <Box display="flex">
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    color="secondary"
                                                    checked={invoiceEnabled}
                                                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                                        setInvoiceEnabled(event.target.checked)
                                                    }
                                                />
                                            }
                                            label={
                                                invoiceEnabled
                                                    ? translate('client-directory:INVOICE_ENABLED')
                                                    : translate('client-directory:INVOICE_DISABLED')
                                            }
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ maxWidth: 900 }}>
                                    <Box sx={{ display: tabState !== '#content' ? 'none' : undefined }}>
                                        <InvoiceContent
                                            labClient={labClient}
                                            currentLab={currentLab}
                                            invoiceEnabled={invoiceEnabled}
                                            setAllInvoiceSettings={handleInvoiceSettingsChange}
                                            onValidityChange={handleContentValidityChange}
                                        />
                                    </Box>
                                    <Box sx={{ display: tabState !== '#settings' ? 'none' : undefined }}>
                                        <InvoiceSettingsForm
                                            labClient={labClient}
                                            currentLab={currentLab}
                                            invoiceEnabled={invoiceEnabled}
                                            setAllInvoiceSettings={handleInvoiceSettingsChange}
                                            onValidityChange={handleSettingsValidityChange}
                                        />
                                    </Box>
                                    <Box sx={{ display: tabState !== '#contactInformation' ? 'none' : undefined }}>
                                        <InvoiceContactInformation
                                            labClient={labClient}
                                            invoiceEnabled={invoiceEnabled}
                                            setAllInvoiceSettings={handleInvoiceSettingsChange}
                                            onValidityChange={handleContactInformationValidityChange}
                                        />
                                    </Box>
                                </Box>
                            </TabContext>
                            <Box>
                                <LoadingButton
                                    variant="contained"
                                    disabled={!isInvoiceSettingsValid}
                                    loading={invoiceSettingsLoading}
                                    onClick={handleSaveInvoiceSettings}
                                >
                                    {translate('common:SAVE')}
                                </LoadingButton>
                            </Box>
                        </Fragment>
                    ) : (
                        <Fragment>
                            <Box mb={1}>
                                <Alert severity="info">{translate('client-directory:INVOICE_DISABLED_INFO')}</Alert>
                            </Box>
                            <Box display="flex">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            color="secondary"
                                            checked={invoiceEnabled}
                                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                                setInvoiceEnabled(event.target.checked)
                                            }
                                        />
                                    }
                                    label={
                                        invoiceEnabled
                                            ? translate('client-directory:INVOICE_ENABLED')
                                            : translate('client-directory:INVOICE_DISABLED')
                                    }
                                />
                            </Box>
                            <Box>
                                <Button variant="contained" onClick={() => invoiceEnabledToggle()}>
                                    {translate('common:SAVE')}
                                </Button>
                            </Box>
                        </Fragment>
                    )}
                </Fragment>
            ) : !labClient.parentHasInvoiceConfig ? (
                <Fragment>
                    <Box sx={{ maxWidth: 900 }}>
                        <Box mb={2}>
                            <Alert severity="info">
                                {translate('client-directory:DISABLED_DUE_TO_PARENT')}
                                &nbsp;
                                {labClient?.customer?.parentClinicId ? (
                                    <Link
                                        component={RouterLink}
                                        to={`/client-directory/clients/${labClient?.customer?.parentClinicId}/invoices${tabState}`}
                                    >
                                        {translate('common:SHOW_CLINIC')}
                                    </Link>
                                ) : undefined}
                            </Alert>
                        </Box>
                        <ClinicInvoiceContent clinic={labClient.customer} />
                    </Box>
                </Fragment>
            ) : null}
        </Fragment>
    );
};

export default Invoices;
